import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Table, TableBody, TableHead, TableRow, Box, Card, IconButton } from '@mui/material';
import { GetApp as GetAppIcon, Delete as DeleteIcon } from '@mui/icons-material';
import CompliancePlip from '../../../../components/CompliancePlip';
import NextStepsAccordion from './NextStepsAccordion';
import MarkAsAddressedButton from './MarkAsAddressedButton';
import MarkAsAddressedModal from './MarkAsAddressedModal';
import { StyledTableCell, StyledTableRow } from '../../../../components/Table.styled';
import { getFullNameOrEmail } from '../../../../redux/user';
import { WaterlineTestReport } from '../WaterlineReports';
import { ComplianceStatus } from '../../../../types';
import { DisplayResultsBy, OpenLogResultModal, Room, WaterlineTestResult } from '../types';
import DeleteTestResultModal from './DeleteTestResultModal';
import ReassignRoomModal from './ReassignRoomModal';
import { formatStandardDate, getTeamMembersIndex, getWaterlineTestCompliance } from '../utils';
import { CustomerState } from '../../../../redux/customer';
import { ChildCustomerState } from '../../../../redux/childCustomer';
import EditResultButton from './EditResultButton';
import useWaterlinePermissions from '../../../../hooks/useWaterlinePermissions';
import useTeamMembers from '../../../../hooks/useTeamMembers';

type TestResultTable = {
  testResults: WaterlineTestResult[],
  displayResultsBy: DisplayResultsBy,
  getTestResults: () => void,
  rooms: Room[],
  getCompliance: () => void,
  customer: CustomerState | ChildCustomerState,
  hasUnmatchedResults?: boolean,
  setOpenModal: Dispatch<SetStateAction<OpenLogResultModal>>,
}

const TestResultTable = (props: TestResultTable) => {
  const {
    testResults,
    displayResultsBy,
    getTestResults,
    rooms,
    getCompliance,
    customer,
    hasUnmatchedResults,
    setOpenModal,
  } = props

  const teamMembers = useTeamMembers()
  const waterLinePermissions = useWaterlinePermissions()  

  const [selectedTestResult, setSelectedTestResult] = useState<WaterlineTestResult | null>(null);
  const [markAsAddressedModalIsOpen, setMarkAsAddressedModalIsOpen] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const hasNextSteps = useMemo(() => {
    for (let i = 0; i < testResults.length; i++) {
      if (testResults[i].next_steps !== null) return true;
    }
    return false;
  }, [testResults]);

  const markAsAddressed = (testResult: WaterlineTestResult) => {
    setSelectedTestResult(testResult);
    setMarkAsAddressedModalIsOpen(true);
  }

  const handleCloseModal = () => {
    setMarkAsAddressedModalIsOpen(false);
    setSelectedTestResult(null);
  }

  const refresh = (r: boolean) => {
    if (r) {
      getTestResults();
      getCompliance();
    }
  }

  const sortedResults = useMemo(() => {
    const results = structuredClone(testResults)

    const callback = displayResultsBy === 'roomChair'
      ? (a: WaterlineTestResult, b: WaterlineTestResult) => new Date(b.test_date ?? '').getTime() - new Date(a.test_date ?? '').getTime()
      : (a: WaterlineTestResult, b: WaterlineTestResult) => (a.location ?? a?.room?.name ?? '').localeCompare(b.location ?? b?.room?.name ?? '')

    results.sort(callback)

    return results
  }, [testResults])

  const complianceResults = useMemo(() => {
    if (displayResultsBy === 'roomChair') {
      const mostRecentTimestamp = new Date(sortedResults[0].test_date ?? '').getTime()

      return sortedResults.filter(result => {
        const timestamp = new Date(result.test_date ?? '').getTime()
        return timestamp === mostRecentTimestamp
      })
    } else {
      return sortedResults
    }
  }, [sortedResults])

  const compliance = useMemo(() => {
    return getWaterlineTestCompliance(complianceResults)
  }, [complianceResults])

  const teamMembersIndex = useMemo(() => {
    return getTeamMembersIndex(teamMembers)
  }, [teamMembers])

  return (
    <div className='m-b-16'>
      <Card variant="outlined" style={{ marginBottom: '15px' }}>
        <div className="box-header flex flex-align-center flex-space-between">
          <div className="flex flex-align-center">
            <CompliancePlip className="m-r-16" status={compliance.status} message={compliance.message} />
            {
              displayResultsBy === 'roomChair' ?
                <Box>Room/Chair: {testResults[0]?.room?.name ?? testResults[0]?.location}</Box>
                :
                <Box>Test Date: {formatStandardDate(testResults[0].test_date)}</Box>
            }
          </div>
          <div className="flex" style={{ alignItems: 'end' }}>
            {
              hasUnmatchedResults
              && waterLinePermissions.roomChair.assign
              && (
                <ReassignRoomModal
                  rooms={rooms}
                  testResults={testResults}
                  onClose={refresh}
                />
              )
            }
            {testResults.length > 0 && (
              <div onClick={() => WaterlineTestReport(testResults, customer)} className="download-box">
                <GetAppIcon fontSize="small" />
              </div>
            )}
          </div>
        </div>
        {hasNextSteps ? <NextStepsAccordion testResults={testResults} /> : null}
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: '3%' }} align="center"></StyledTableCell>
              <StyledTableCell sx={{ width: '5%' }} align="center">Result</StyledTableCell>
              <StyledTableCell sx={{ width: '9%' }} align="center">In-Office/Mail-In</StyledTableCell>
              <StyledTableCell sx={{ width: '8%' }} align="center">Test/Retest</StyledTableCell>
              <StyledTableCell sx={{ width: '5%' }} align="center">Sample</StyledTableCell>
              <StyledTableCell sx={{ width: '7%' }} align="center">Test Type</StyledTableCell>
              {displayResultsBy === 'roomChair' && <StyledTableCell sx={{ width: '7%' }} align="center">Test Date</StyledTableCell>}
              <StyledTableCell sx={{ width: '6%' }} align="center">Room/Chair</StyledTableCell>
              <StyledTableCell sx={{ width: '8%' }} align="center">Daily Treatment Method</StyledTableCell>
              <StyledTableCell sx={{ width: '8%' }} align="center">Description</StyledTableCell>
              <StyledTableCell sx={{ width: '8%' }} align="center">Team Member/Lab Tech</StyledTableCell>
              <StyledTableCell sx={{ width: '4%' }} align="center">pH</StyledTableCell>
              <StyledTableCell sx={{ width: '4%' }} align="center">TDS</StyledTableCell>
              <StyledTableCell sx={{ width: '6%' }} align="center">CFU/ML</StyledTableCell>
              <StyledTableCell sx={{ width: '8%' }} align="center">Corrective Action</StyledTableCell>
              <StyledTableCell sx={{ width: '6%' }} align="center"></StyledTableCell>
              <StyledTableCell sx={{ width: '3%' }} align="center"></StyledTableCell>
              <StyledTableCell sx={{ width: '4%' }} align="center"></StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedResults
              .map(testResult => (
                <StyledTableRow key={testResult.test_result_id}>
                  <StyledTableCell align="center">
                    <CompliancePlip className="m-r-8" status={testResult.is_pass ? ComplianceStatus.Passed : ComplianceStatus.Failed} message={`${testResult.retest ? 'Retest' : ''} ${testResult.is_pass ? 'Passed' : 'Failed'}`} />
                  </StyledTableCell>
                  <StyledTableCell align="center">{testResult.is_pass ? 'Pass' : 'Fail'}</StyledTableCell>
                  <StyledTableCell align="center">{testResult.in_office_test ? 'In-Office' : 'Mail-In'}</StyledTableCell>
                  <StyledTableCell align="center">{testResult.retest ? 'Retest' : 'Test'}</StyledTableCell>
                  <StyledTableCell align="center">{testResult.sample_number}</StyledTableCell>
                  <StyledTableCell align="center">{testResult.hpc_method}</StyledTableCell>
                  {displayResultsBy === 'roomChair' && <StyledTableCell align="center">{formatStandardDate(testResult.test_date)}</StyledTableCell>}
                  <StyledTableCell align="center">{testResult.location ?? testResult?.room?.name}</StyledTableCell>
                  <StyledTableCell align="center">{testResult.product}</StyledTableCell>
                  <StyledTableCell align="center">{testResult.result_description}</StyledTableCell>
                  <StyledTableCell sx={{ wordWrap: 'break-word' }} align="center">{testResult.tested_by ? getFullNameOrEmail(teamMembersIndex[testResult.tested_by as number]) : testResult.test_operator}</StyledTableCell>
                  <StyledTableCell align="center">{testResult.pH}</StyledTableCell>
                  <StyledTableCell align="center">{testResult.TDS}</StyledTableCell>
                  <StyledTableCell align="center">{testResult.in_office_test ? (Number(testResult.cfu) === 499 ? 'Pass (<500 CFUs)' : 'Fail (>=500 CFUs)') : testResult.cfu}</StyledTableCell>
                  <StyledTableCell align="center">{testResult.addressed_note}</StyledTableCell>
                  <StyledTableCell>
                    {!testResult.is_pass ? <MarkAsAddressedButton testResult={testResult} markAsAddressed={markAsAddressed} /> : null}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {
                      testResult.in_office_test
                        && waterLinePermissions.inOffice.edit
                        && <EditResultButton testResult={testResult} setOpenModal={setOpenModal} />
                    }                    
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {
                      testResult.in_office_test
                      && waterLinePermissions.inOffice.delete
                      && (
                        <IconButton
                          onClick={() => {
                            setShowDeleteModal(true);
                            setSelectedTestResult(testResult);
                          }}
                          sx={{
                            width: '16px',
                            height: '16px'
                          }}
                        >
                          <DeleteIcon
                            fontSize="small"
                            sx={{
                              width: '16px'
                            }}
                          />
                        </IconButton>
                      )
                    }
                    {!testResult.in_office_test && testResult?.room?.name && (
                      <ReassignRoomModal
                        rooms={rooms}
                        testResults={[testResult]}
                        onClose={refresh}
                        currentRoom={testResult.room}
                        icon={true}
                      />
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
        <MarkAsAddressedModal
          testResult={selectedTestResult}
          open={markAsAddressedModalIsOpen}
          handleClose={handleCloseModal}
          getTestResults={getTestResults}
        />
        <DeleteTestResultModal
          onClose={(refresh) => {
            if (refresh) {
              getTestResults();
              getCompliance();
            }
            setShowDeleteModal(false);
          }}
          isOpen={showDeleteModal}
          testResult={selectedTestResult}
        />
      </Card>
    </div>
  );
}

export default TestResultTable
